import { useEffect, useRef, useState, type RefObject } from 'react';
import type { Settings } from '@core/settings/schema.ts';
import {
  getShadowingCenterOffsetPx,
  resetShadowingCenterOffset,
  setShadowingCenterOffsetPx,
} from '@core/dubbing/shadowingSession.ts';
import { clearTimingPreviewMs } from '@core/dubbing/timingPreview.ts';
import type { SettingsStore } from '@ports/SettingsStore.ts';
import type { VideoController } from '@ports/VideoController.ts';
import { installDubbingTimingDrag } from '../../../extension/src/adapters/dom/dubbingTimingDrag.ts';
import { installShadowingHandleLaneReveal } from '../../../extension/src/adapters/dom/shadowingHandleLaneReveal.ts';
import { installSubtitlePositionDrag } from '../../../extension/src/adapters/dom/subtitlePositionDrag.ts';
import { useInputCapabilities } from '../hooks/useInputCapabilities.ts';
import { installHoverReveal } from '../features/overlay/installHoverReveal.ts';
import { useI18n } from '../i18n/index.ts';

/** Temporarily off — re-enable with Settings toggle when shipping hover-to-reveal. */
const HOVER_REVEAL_UI_ENABLED = false;

export type SubtitleOverlayProps = {
  /** Host for imperative OverlayPainter (#mainSub / #secondSub). */
  hostRef: RefObject<HTMLDivElement>;
  settings: Settings;
  settingsStore?: SettingsStore;
  video?: VideoController | null;
  /** Called when the host mounts so sync can paint into it. */
  onHostReady?: () => void;
  /** Force sync re-paint (e.g. shadowing center-line offset). */
  onInvalidate?: () => void;
};

/**
 * Overlay host only — cue DOM + tops are painted by OverlayPainter from
 * SubtitleSyncEngine (same path as extension OverlayRenderer), not React.
 */
export function SubtitleOverlay({
  hostRef,
  settings,
  settingsStore,
  video,
  onHostReady,
  onInvalidate,
}: SubtitleOverlayProps) {
  const { canHover, coarsePointer } = useInputCapabilities();
  const { t } = useI18n();
  const hoverRevealEnabled = HOVER_REVEAL_UI_ENABLED && settings.hoverToReveal;
  /** Touch host starts hidden; desktop stays visible until first mousemove (extension). */
  const touchHostMode = coarsePointer && !canHover;
  const [revealed, setRevealed] = useState(() => !(hoverRevealEnabled && touchHostMode));
  const invalidateRef = useRef(onInvalidate);
  invalidateRef.current = onInvalidate;

  useEffect(() => {
    if (!hoverRevealEnabled) {
      setRevealed(true);
      return;
    }
    // Extension: do not force-hide until the first mousemove.
    // Touch: hide until the reveal chip (no hover pointer).
    setRevealed(!touchHostMode);
  }, [hoverRevealEnabled, touchHostMode]);

  useEffect(() => {
    onHostReady?.();
  }, [onHostReady]);

  useEffect(() => {
    if (!settingsStore || !video) return undefined;

    const liveSettings = () => settingsStore.getSnapshot?.() ?? settings;

    const installTiming = () => installDubbingTimingDrag({
      isDubbingMode: () => liveSettings().practiceMode === 'shadowing',
      getCurrentTimeMs: () => video.getCurrentTimeMs(),
      getDurationMs: () => video.getDurationMs(),
      seekMs: (ms) => video.seekMs(ms),
      isPaused: () => video.isPaused(),
      pause: () => video.pause(),
      play: () => {
        void video.play();
      },
      onPreview: () => {
        invalidateRef.current?.();
      },
      whenSeekSettled: (done) => video.on('seeked', done),
    });

    const disposePosition = installSubtitlePositionDrag({
      getSettings: () => {
        const s = liveSettings();
        return {
          subY: s.subY,
          mainSubY: s.mainSubY,
          secondSubY: s.secondSubY,
          subtitleSeparation: s.subtitleSeparation,
        };
      },
      // Shadowing center clamp uses playback width (avoid viewport/playback mismatch).
      getViewportRect: () => {
        if (liveSettings().practiceMode === 'shadowing') {
          const p = video.getPlaybackClientRect();
          return { width: p.width, height: p.height };
        }
        return video.getViewportRect();
      },
      applyLocal: (patch) => {
        void settingsStore.set(patch);
      },
      persist: (patch) => {
        void settingsStore.set(patch);
      },
      isShadowingMode: () => liveSettings().practiceMode === 'shadowing',
      getShadowingCenterOffsetPx: (slot) => (
        getShadowingCenterOffsetPx(slot, Boolean(liveSettings().subtitleSeparation))
      ),
      setShadowingCenterOffsetPx: (slot, offsetPx) => {
        setShadowingCenterOffsetPx(slot, Boolean(liveSettings().subtitleSeparation), offsetPx);
        invalidateRef.current?.();
      },
    });

    const disposeLane = installShadowingHandleLaneReveal({
      isShadowingMode: () => liveSettings().practiceMode === 'shadowing',
    });

    let disposeTiming = installTiming();

    let prevMode = liveSettings().practiceMode;
    const unsubSettings = settingsStore.subscribe((next) => {
      if (prevMode === 'shadowing' && next.practiceMode !== 'shadowing') {
        resetShadowingCenterOffset();
        // Abort in-flight scrub / pending settle (restores play) and reinstall.
        disposeTiming();
        disposeTiming = installTiming();
        clearTimingPreviewMs();
        invalidateRef.current?.();
      }
      prevMode = next.practiceMode;
    });

    return () => {
      unsubSettings();
      disposePosition();
      disposeLane();
      disposeTiming();
      if (prevMode === 'shadowing') {
        resetShadowingCenterOffset();
        clearTimingPreviewMs();
      }
    };
  }, [settingsStore, video]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !hoverRevealEnabled) return undefined;
    if (canHover) {
      return installHoverReveal({
        getEnabled: () => settings.hoverToReveal,
        setRevealed,
        mode: 'per-slot',
      });
    }
    // Touch (and rare no-hover / no-coarse): host-level reveal chip path.
    return installHoverReveal({
      getEnabled: () => settings.hoverToReveal,
      setRevealed,
      mode: 'host',
    });
  }, [hostRef, hoverRevealEnabled, canHover, settings.hoverToReveal]);

  const showTouchReveal = hoverRevealEnabled && !canHover && !revealed;
  const revealMode = canHover ? 'hover' : 'touch';

  return (
    <>
      <div
        ref={hostRef}
        className="subtitle-overlay"
        data-testid="subtitle-overlay"
        data-reveal-mode={revealMode}
        data-revealed={revealed ? 'true' : 'false'}
        aria-hidden={!revealed}
      />
      {showTouchReveal ? (
        <button
          type="button"
          className="subtitle-overlay__reveal"
          data-testid="subtitle-reveal"
          onClick={() => setRevealed(true)}
        >
          {t('hoverRevealShow')}
        </button>
      ) : null}
    </>
  );
}
